import { DynamicMapMenuItems } from "@/features/dynamic-overview-map/dynamic-map-menu-items";
import { curryAppSelector } from "@/store/reselect";
import { grey, NoTranslate } from "@faro-lotv/flat-ui";
import {
  GUID,
  IElementGenericImgSheet,
  isIElementAreaSection,
  isIElementOverviewImage,
} from "@faro-lotv/ielement-types";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import { Divider } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { ReactNode, type JSX } from "react";
import { MultiSheetMenuItemProps } from "./multi-sheet-menu-item";

export type MultiSheetMenuItemGroupProps = {
  /** The contents of the group */
  group: MultiSheetMenuItemGroup;

  /** A callback to compute the visibility of a sheet */
  isSheetVisible(sheetId: GUID): boolean;

  /** Whether the item is the first one in the list */
  isFirstGroup: boolean;

  /** Number of visible sheets in multi-sheet menu for selected area  */
  visibleSheetsInArea: number;

  /** Total number of sheets in multi-sheet menu for selected area  */
  sheetsInArea: number;
} & Pick<
  MultiSheetMenuItemProps,
  "listVisibleSheets" | "onElementClicked" | "onEyeClicked" | "showEditOptions"
>;

/**
 * @returns a group of sheets with a title
 */
export function MultiSheetMenuItemGroup({
  group,
  isFirstGroup,
  sheetsInArea,
  visibleSheetsInArea,
  listVisibleSheets,
  showEditOptions = true,
  isSheetVisible,
  onElementClicked,
  onEyeClicked,
}: MultiSheetMenuItemGroupProps): JSX.Element {
  return (
    <>
      {!isFirstGroup && (
        <Divider
          sx={{
            backgroundColor: grey[800],
            // Important is needed to override a inherited style from being in a MuiListItem
            my: "0 !important",
          }}
        />
      )}

      {group.sheets.map((sheet) => (
        <DynamicMapMenuItems
          sheet={sheet}
          key={sheet.id}
          sheetsInArea={sheetsInArea}
          visibleSheetsInArea={visibleSheetsInArea}
          listVisibleSheets={listVisibleSheets}
          isVisible={isSheetVisible(sheet.id)}
          onElementClicked={onElementClicked}
          onEyeClicked={onEyeClicked}
          showEditOptions={showEditOptions}
        />
      ))}
    </>
  );
}

type MultiSheetMenuItemGroup = {
  /** The title of the group */
  title: ReactNode;

  /** The sheets to show in this group */
  sheets: IElementGenericImgSheet[];
};

export const selectMultiSheetMenuItemGroups = curryAppSelector(
  createSelector(
    [(_state, sheets: IElementGenericImgSheet[]) => sheets, (state) => state],
    (sheets, state): MultiSheetMenuItemGroup[] => {
      // Map of areaId to sheets in that area
      const areaSheets = new Map<GUID, IElementGenericImgSheet[]>();
      const overviewMapSheets: IElementGenericImgSheet[] = [];
      // This currently shouldn't contain any sheets, but this makes sure that unexpected sheets still have a place to be shown.
      const otherSheets: IElementGenericImgSheet[] = [];

      for (const sheet of sheets) {
        if (isIElementOverviewImage(sheet)) {
          overviewMapSheets.push(sheet);
        } else {
          const area = selectAncestor(sheet, isIElementAreaSection)(state);

          if (area) {
            const existingGroup = areaSheets.get(area.id);
            if (existingGroup) {
              existingGroup.push(sheet);
            } else {
              areaSheets.set(area.id, [sheet]);
            }
          } else {
            otherSheets.push(sheet);
          }
        }
      }

      return [
        ...Array.from(areaSheets.entries()).map(([areaId, sheets]) => ({
          title: (
            <NoTranslate component="span">
              {selectIElement(areaId)(state)?.name ?? areaId}
            </NoTranslate>
          ),
          sheets,
        })),
        ...(overviewMapSheets.length
          ? [{ title: "Overview Maps", sheets: overviewMapSheets }]
          : []),
        ...(otherSheets.length
          ? [{ title: "Other", sheets: otherSheets }]
          : []),
      ];
    },
  ),
);
